/**
 * VTK Module — прямой доступ к vtk.js API
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v10 — ULTIMATE VTK SUITE]:
 *  - Все исправления v9 сохранены
 *  - Vessel Branch Detection: автоматическое определение бифуркаций
 *  - Multi-vessel CPR: одновременная CPR для нескольких сосудов
 *  - Stent Planning: автоматический расчёт диаметра и длины стента
 *  - Аневризма: измерение шейки (neck diameter)
 *  - 3D Measurements on Mesh: расстояние/угол на 3D-модели
 *  - Mesh Decimation: уменьшение полигонов (vtkDecimatePro)
 *  - Mesh Smoothing: Laplacian/Taubin smoothing
 *  - OBJ с текстурами: UV-координаты и MTL
 *  - GLTF/GLB Export: современный формат
 *  - VRML Export: для 3D-печати
 */

import { log } from '../utils/logger.js';   // [11.1.44] провалы — в ЖУРНАЛ, не в console

let vtklib = null;
let _vtkPromise = null;

/**
 * [11.1.42] ВОТ ПОЧЕМУ STL НЕ СТРОИЛСЯ НИ РАЗУ.
 *
 * Здесь стояло `import('@kitware/vtk.js/vtk.js')` — ЦЕЛЬНЫЙ бандл vtk.js.
 * В этой сборке его нет: проект подключает vtk ПОИМЁННЫМИ модулями, и это
 * прямо записано в передаче дел («window.vtk НЕ существует, брать
 * viewport.getVtkPlaneClass()»). Так делают и viewport.js:154, и
 * viewportEffects.js:199, и рабочий Seg3D в clinicalTools.js:1531. Один
 * этот файл ходил другой дорогой — импорт падал, runMarchingCubes ловил
 * исключение и возвращал ПУСТОЙ объект, а наверху это читалось как
 * «Поверхность по маске не построилась». Владелец спрашивал трижды;
 * прошлая правка (вырезка по рамке) чинила память, но упиралась в ту же
 * несуществующую дверь.
 *
 * Теперь классы грузятся поимённо и складываются в тот же по форме объект,
 * которым пользуется остальной файл (vtk.vtkImageData.newInstance() и т.д.),
 * — ни одна из сорока функций модуля не переписывается.
 */
async function getVtk() {
  if (vtklib) return vtklib;
  if (_vtkPromise) return _vtkPromise;

  _vtkPromise = (async () => {
    // [11.1.44] ТОЧНАЯ ПРАВДА О ПЕРВОПРИЧИНЕ, сверенная на установленном
    // пакете: файл '@kitware/vtk.js/vtk.js' СУЩЕСТВУЕТ и грузится, но отдаёт
    // только функцию-фабрику vtk и vtkGlobal — НИ ОДНОГО класса. То есть
    // vtklib.vtkImageData был undefined, и первый же вызов
    // vtk.vtkImageData.newInstance() бросал TypeError прямо в catch. Мой
    // прошлый диагноз «бандла нет» был неточен: бандл есть, но пустой для
    // наших целей. Классы живут только в поимённых модулях.
    //
    // [11.1.43] ПУТИ — СТАТИЧЕСКИМИ ЛИТЕРАЛАМИ. Правка 11.1.42 грузила те же
    // модули, но через переменную: `import(/* @vite-ignore */ путь)`. Сборщик
    // такое не разрешает и не кладёт модуль в бандл — браузер честно не
    // находил «@kitware/vtk.js/...» как URL. Журнал владельца показал это
    // дословно: «не загрузились базовые модули». Литералы бандлер видит.
    const тихо = (p) => p.catch(() => null);
    const [
      ImageData, DataArray, MarchingCubes, PolyData, Points,
      Smooth, STLWriter, Plane, Mapper, Actor, Consts,
    ] = await Promise.all([
      тихо(import('@kitware/vtk.js/Common/DataModel/ImageData')),
      тихо(import('@kitware/vtk.js/Common/Core/DataArray')),
      тихо(import('@kitware/vtk.js/Filters/General/ImageMarchingCubes')),
      тихо(import('@kitware/vtk.js/Common/DataModel/PolyData')),
      тихо(import('@kitware/vtk.js/Common/Core/Points')),
      тихо(import('@kitware/vtk.js/Filters/General/WindowedSincPolyDataFilter')),
      тихо(import('@kitware/vtk.js/IO/Geometry/STLWriter')),
      тихо(import('@kitware/vtk.js/Common/DataModel/Plane')),
      тихо(import('@kitware/vtk.js/Rendering/Core/Mapper')),
      тихо(import('@kitware/vtk.js/Rendering/Core/Actor')),
      тихо(import('@kitware/vtk.js/Common/Core/DataArray/Constants')),
    ]);
    const д = (m) => (m ? (m.default || m) : null);
    if (!д(ImageData) || !д(DataArray) || !д(MarchingCubes)) {
      _vtkPromise = null;
      throw new Error('vtk.js: не загрузились базовые модули (ImageData/DataArray/ImageMarchingCubes)');
    }
    // ВАЖНО: VtkDataTypes.SHORT — это СТРОКА 'Int16Array', а не число
    // (сверено с Common/Core/DataArray/Constants.js установленного пакета).
    // Прошлая заглушка {SHORT: 4} создала бы битый массив скаляров.
    const типы = (Consts?.VtkDataTypes || Consts?.default?.VtkDataTypes)
      || { SHORT: 'Int16Array', UNSIGNED_CHAR: 'Uint8Array', FLOAT: 'Float32Array', DOUBLE: 'Float64Array' };
    // vtkDecimatePro в этой версии пакета ОТСУТСТВУЕТ — оставляем null, и
    // decimateMesh честно вернёт исходную сетку вместо падения.
    vtklib = {
      vtkImageData: д(ImageData), vtkDataArray: д(DataArray), vtkImageMarchingCubes: д(MarchingCubes),
      vtkPolyData: д(PolyData), vtkPoints: д(Points), vtkDecimatePro: null,
      vtkSmoothPolyDataFilter: д(Smooth), vtkSTLWriter: д(STLWriter),
      vtkPlane: д(Plane), vtkMapper: д(Mapper), vtkActor: д(Actor),
      VtkDataTypes: типы,
    };
    return vtklib;
  })();
  return _vtkPromise;
}

// ══════════════════════════════════════════════════════════════
//  ВНУТРЕННИЕ УТИЛИТЫ
// ══════════════════════════════════════════════════════════════

class MinHeap {
  constructor(capacity = 65536) {
    this.heapF = new Float64Array(capacity);
    this.heapG = new Float32Array(capacity);
    this.heapIdx = new Int32Array(capacity);
    this.size = 0;
    this.capacity = capacity;
  }
  push(f, g, idx) {
    if (this.size >= this.capacity) this._grow();
    let i = this.size++;
    this.heapF[i] = f; this.heapG[i] = g; this.heapIdx[i] = idx;
    this._bubbleUp(i);
  }
  pop() {
    if (this.size === 0) return null;
    const result = { f: this.heapF[0], g: this.heapG[0], val: this.heapIdx[0] };
    this.size--;
    if (this.size > 0) {
      this.heapF[0] = this.heapF[this.size]; this.heapG[0] = this.heapG[this.size]; this.heapIdx[0] = this.heapIdx[this.size];
      this._sinkDown(0);
    }
    return result;
  }
  get length() { return this.size; }
  _bubbleUp(i) {
    const f = this.heapF, g = this.heapG, idx = this.heapIdx;
    const fi = f[i], gi = g[i], idxi = idx[i];
    while (i > 0) { const p = (i-1)>>1; if (f[p] <= fi) break; f[i]=f[p]; g[i]=g[p]; idx[i]=idx[p]; i=p; }
    f[i]=fi; g[i]=gi; idx[i]=idxi;
  }
  /**
   * [10.19.11] ЗДЕСЬ БЫЛА ОШИБКА, ИЗ-ЗА КОТОРОЙ КУЧА ОТДАВАЛА НЕ МИНИМУМ.
   *
   * Приём «просеивания с дыркой» такой: значение, которое опускаем, держим в
   * переменной fi, а в массиве на его место поднимаем детей. Прежний код
   * сравнивал детей с `f[smallest]`, где smallest на втором и дальше витке
   * равен i, — а в f[i] к этому моменту уже лежит ПОДНЯТЫЙ ребёнок, а не fi.
   * То есть сравнение шло не с тем значением, и свойство кучи нарушалось.
   *
   * Последствие было не абстрактным: на этой куче стоит поиск пути A* для
   * центральной линии сосуда (extractCenterline). Неверный порядок извлечения
   * означает, что A* берёт узлы не по возрастанию оценки и возвращает
   * НЕОПТИМАЛЬНЫЙ путь. Измерено тестом cpr_test на прямой трубке: путь
   * получался 76 мм вместо 70 — то есть линия виляла внутри сосуда, а длина
   * (по ней планируют стент) завышалась на 8,6%.
   *
   * Правильно — сравнивать детей с fi, а не с содержимым текущей ячейки.
   */
  _sinkDown(i) {
    const f = this.heapF, g = this.heapG, idx = this.heapIdx;
    const fi = f[i], gi = g[i], idxi = idx[i]; const len = this.size;
    while (true) {
      const left = 2*i+1, right = 2*i+2;
      let меньший = -1, лучшее = fi;
      if (left < len && f[left] < лучшее) { меньший = left; лучшее = f[left]; }
      if (right < len && f[right] < лучшее) { меньший = right; лучшее = f[right]; }
      if (меньший === -1) break;
      f[i]=f[меньший]; g[i]=g[меньший]; idx[i]=idx[меньший]; i=меньший;
    }
    f[i]=fi; g[i]=gi; idx[i]=idxi;
  }
  _grow() {
    const newCap = this.capacity*2;
    const newF=new Float64Array(newCap), newG=new Float32Array(newCap), newIdx=new Int32Array(newCap);
    newF.set(this.heapF); newG.set(this.heapG); newIdx.set(this.heapIdx);
    this.heapF=newF; this.heapG=newG; this.heapIdx=newIdx; this.capacity=newCap;
  }
}

const vec3 = {
  add: (a,b) => [a[0]+b[0], a[1]+b[1], a[2]+b[2]],
  sub: (a,b) => [a[0]-b[0], a[1]-b[1], a[2]-b[2]],
  cross: (a,b) => [a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]],
  normalize: (v) => { const len=Math.sqrt(v[0]**2+v[1]**2+v[2]**2)||1; return [v[0]/len, v[1]/len, v[2]/len]; },
  dist: (a,b) => Math.sqrt((b[0]-a[0])**2+(b[1]-a[1])**2+(b[2]-a[2])**2),
  dot: (a,b) => a[0]*b[0]+a[1]*b[1]+a[2]*b[2],
  angle: (a,b) => { const d=vec3.dot(a,b)/(Math.sqrt(vec3.dot(a,a))*Math.sqrt(vec3.dot(b,b))||1); return Math.acos(Math.min(1,Math.max(-1,d)))*180/Math.PI; },
};

async function createVtkImageData(pixels, dims, spacing, origin = [0,0,0]) {
  const vtk = await getVtk();
  const vtkImageData = vtk.vtkImageData.newInstance();
  vtkImageData.setDimensions(dims); vtkImageData.setSpacing(spacing); vtkImageData.setOrigin(origin);
  let values;
  if (pixels instanceof Int16Array) { values = pixels; }
  else { values = new Int16Array(pixels.length); for (let i=0; i<pixels.length; i++) { const v=pixels[i]; values[i] = v>32767?32767:v<-32768?-32768:Math.round(v); } }
  const dataArray = vtk.vtkDataArray.newInstance({ numberOfComponents:1, values, dataType: vtk.VtkDataTypes.SHORT });
  dataArray.setName('Scalars'); vtkImageData.getPointData().setScalars(dataArray);
  return { vtkImageData, dataArray };
}

// ══════════════════════════════════════════════════════════════
// 1. MARCHING CUBES
// ══════════════════════════════════════════════════════════════

export async function runMarchingCubes(pixels, dims, spacing, isoValue = 150, origin = [0, 0, 0]) {
  try {
    const vtk = await getVtk();
    // [11.1.41] origin проведён наружу: сетку теперь строят по ВЫРЕЗКЕ вокруг
    // органа (по всему тому не хватало памяти — см. organView.stlОргана), а
    // вырезка стоит не в нуле, и без смещения STL уехал бы от пациента.
    const { vtkImageData, dataArray } = await createVtkImageData(pixels, dims, spacing, origin);
    const mc = vtk.vtkImageMarchingCubes.newInstance({ contourValue: isoValue, computeNormals: true, mergePoints: true });
    mc.setInputData(vtkImageData); mc.update();
    const polyData = mc.getOutputData();
    const points = Float32Array.from(polyData.getPoints().getData());
    const polys = Uint32Array.from(polyData.getPolys().getData());
    const normalsData = polyData.getPointData().getNormals();
    const normals = normalsData ? Float32Array.from(normalsData.getData()) : new Float32Array(0);
    const pointCount = polyData.getNumberOfPoints(), triangleCount = polyData.getNumberOfCells();
    mc.delete(); polyData.delete(); dataArray.delete(); vtkImageData.delete();
    return { pointCount, triangleCount, points, polys, normals };
  } catch(e) { log.warn('[vtkModule] runMarchingCubes:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// 1b. MESH DECIMATION (уменьшение полигонов)
// ══════════════════════════════════════════════════════════════

export async function decimateMesh(meshResult, targetReduction = 0.5) {
  try {
    const vtk = await getVtk();
    const polyData = _createPolyDataFromMesh(vtk, meshResult);
    if (!vtk.vtkDecimatePro?.newInstance) { log.warn('[vtkModule] vtkDecimatePro нет в этой версии пакета — сетка отдана как есть'); return meshResult; }
    const decimator = vtk.vtkDecimatePro.newInstance({ targetReduction });
    decimator.setInputData(polyData); decimator.update();
    const result = decimator.getOutputData();
    const points = Float32Array.from(result.getPoints().getData());
    const polys = Uint32Array.from(result.getPolys().getData());
    const normalsData = result.getPointData().getNormals();
    const normals = normalsData ? Float32Array.from(normalsData.getData()) : new Float32Array(0);
    const pointCount = result.getNumberOfPoints(), triangleCount = result.getNumberOfCells();
    decimator.delete(); result.delete(); polyData.delete();
    return { pointCount, triangleCount, points, polys, normals };
  } catch(e) { log.warn('[vtkModule] decimateMesh:', e.message); return meshResult; }
}

// ══════════════════════════════════════════════════════════════
// 1c. MESH SMOOTHING (Laplacian)
// ══════════════════════════════════════════════════════════════

export async function smoothMesh(meshResult, iterations = 20, relaxationFactor = 0.1) {
  try {
    const vtk = await getVtk();
    const polyData = _createPolyDataFromMesh(vtk, meshResult);
    const smoother = vtk.vtkSmoothPolyDataFilter.newInstance({ numberOfIterations: iterations, relaxationFactor });
    smoother.setInputData(polyData); smoother.update();
    const result = smoother.getOutputData();
    const points = Float32Array.from(result.getPoints().getData());
    const polys = Uint32Array.from(result.getPolys().getData());
    const normalsData = result.getPointData().getNormals();
    const normals = normalsData ? Float32Array.from(normalsData.getData()) : new Float32Array(0);
    const pointCount = result.getNumberOfPoints(), triangleCount = result.getNumberOfCells();
    smoother.delete(); result.delete(); polyData.delete();
    return { pointCount, triangleCount, points, polys, normals };
  } catch(e) { log.warn('[vtkModule] smoothMesh:', e.message); return meshResult; }
}

// ══════════════════════════════════════════════════════════════
// 2. STL EXPORT
// ══════════════════════════════════════════════════════════════

function _createPolyDataFromMesh(vtk, mesh) {
  const polyData = vtk.vtkPolyData.newInstance();
  const pointArray = vtk.vtkPoints.newInstance(); pointArray.setData(mesh.points, 3); polyData.setPoints(pointArray);
  polyData.getPolys().setData(mesh.polys);
  if (mesh.normals?.length>0) {
    const na = vtk.vtkDataArray.newInstance({ numberOfComponents:3, values:mesh.normals, dataType:vtk.VtkDataTypes.FLOAT });
    na.setName('Normals'); polyData.getPointData().setNormals(na);
  }
  return polyData;
}

/**
 * [12.0.30] Готовый актёр из сетки — для показа органа ПОВЕРХ снимка в
 * объёмном окне (core/organSurface3D.js). Классы Mapper и Actor тут уже
 * загружены getVtk(); нет их в сборке — честно возвращаем null, вызывающий
 * скажет об этом врачу.
 *
 * @param {{points:Float32Array, polys:Uint32Array, normals?:Float32Array}} mesh
 * @param {{цвет?:number[], прозрачность?:number, блеск?:number,
 *          цветаВершин?:Uint8Array|null, материал?:{ambient:number, diffuse:number, specular:number, specularPower:number}}} [о]
 *   цвет [r,g,b] 0…1; [12.0.36] цветаВершин — r,g,b 0…255 на вершину (окраска
 *   по снимку), материал — свойства света (organSurfaceCore.МАТЕРИАЛ_ТКАНИ)
 */
export async function актёрИзСетки(mesh, о = {}) {
  const vtk = await getVtk();
  if (!vtk.vtkMapper?.newInstance || !vtk.vtkActor?.newInstance) {
    log.warn('[vtkModule] Mapper/Actor нет в этой сборке — поверхность не показать');
    return null;
  }
  const polyData = _createPolyDataFromMesh(vtk, mesh);
  const mapper = vtk.vtkMapper.newInstance();
  mapper.setInputData(polyData);
  // [12.0.36] Цвет по вершинам — прямыми значениями RGB, без таблицы цветов.
  const точек = Math.trunc((mesh.points?.length || 0) / 3);
  const цв = о.цветаВершин;
  if (цв && цв.length === 3 * точек && точек > 0) {
    const ца = vtk.vtkDataArray.newInstance({ numberOfComponents: 3, values: цв, dataType: vtk.VtkDataTypes.UNSIGNED_CHAR });
    ца.setName('ЦветТкани');
    polyData.getPointData().setScalars(ца);
    mapper.setScalarVisibility?.(true);
    if (typeof mapper.setColorModeToDirectScalars === 'function') mapper.setColorModeToDirectScalars();
    else mapper.setColorMode?.(2);   // ColorMode.DIRECT_SCALARS
    mapper.setScalarModeToUsePointData?.();
    mapper.setInterpolateScalarsBeforeMapping?.(false);
  } else {
    mapper.setScalarVisibility?.(false);
  }
  const actor = vtk.vtkActor.newInstance();
  actor.setMapper(mapper);
  const [r, g, b] = Array.isArray(о.цвет) && о.цвет.length === 3 ? о.цвет : [0.9, 0.3, 0.3];
  const св = actor.getProperty();
  св.setColor(r, g, b);
  св.setOpacity(Number.isFinite(о.прозрачность) ? о.прозрачность : 1);
  const мат = о.материал || null;
  св.setSpecular?.(Number.isFinite(мат?.specular) ? мат.specular : (Number.isFinite(о.блеск) ? о.блеск : 0.2));
  св.setSpecularPower?.(Number.isFinite(мат?.specularPower) ? мат.specularPower : 20);
  св.setDiffuse?.(Number.isFinite(мат?.diffuse) ? мат.diffuse : 0.85);
  св.setAmbient?.(Number.isFinite(мат?.ambient) ? мат.ambient : 0.25);
  return actor;
}

export async function exportSTL(meshData, filename = 'model.stl') {
  try {
    const vtk = await getVtk();
    let polyData;
    if (meshData.isA && meshData.isA('vtkPolyData')) { polyData = meshData; }
    else if (meshData.points && meshData.polys) { polyData = _createPolyDataFromMesh(vtk, meshData); }
    else throw new Error('Invalid mesh data');
    const writer = vtk.vtkSTLWriter.newInstance();
    // [11.1.45] setBinary в этой версии vtk.js НЕ СУЩЕСТВУЕТ (есть setFormat)
    // — вызов бросал TypeError: «файл не записался». ASCII проверен на пакете.
    writer.setInputData(polyData);
    const outputArray = writer.getOutputData();
    const blob = new Blob([outputArray], { type:'model/stl' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
    writer.delete(); if (polyData!==meshData) polyData.delete();
    return { success:true, sizeKb:Math.round((outputArray.byteLength || outputArray.length || 0)/1024) };
  } catch(e) { log.warn('[vtkModule] exportSTL:', e.message); return {}; }
}

export async function pixelsToSTL(pixels, dims, spacing, isoValue=150, filename='model.stl') {
  try {
    const vtk = await getVtk();
    const { vtkImageData, dataArray } = await createVtkImageData(pixels, dims, spacing);
    const mc = vtk.vtkImageMarchingCubes.newInstance({ contourValue:isoValue });
    mc.setInputData(vtkImageData);
    const writer = vtk.vtkSTLWriter.newInstance();
    writer.setInputConnection(mc.getOutputPort());   // [11.1.45] setBinary похоронен и здесь
    const outputArray = writer.getOutputData();
    const blob = new Blob([outputArray], { type:'model/stl' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
    writer.delete(); mc.delete(); dataArray.delete(); vtkImageData.delete();
    return { success:true, sizeKb:Math.round((outputArray.byteLength || outputArray.length || 0)/1024) };
  } catch(e) { log.warn('[vtkModule] pixelsToSTL:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// 2b. GLTF/GLB EXPORT
// ══════════════════════════════════════════════════════════════

export async function exportGLTF(meshResult, filename = 'model.gltf', binary = false) {
  try {
    const { points, polys, normals } = meshResult;
    if (!points?.length) throw new Error('No mesh data');

    const gltf = {
      asset: { version: '2.0', generator: 'Mikhmel Cornerstone4D v6' },
      scene: 0, scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0 }],
      meshes: [{
        primitives: [{
          attributes: { POSITION: 0 },
          indices: 1,
        }],
      }],
      accessors: [
        { bufferView: 0, componentType: 5126, count: points.length / 3, type: 'VEC3', min: [Infinity,Infinity,Infinity], max: [-Infinity,-Infinity,-Infinity] },
        { bufferView: 1, componentType: 5125, count: polys.length, type: 'SCALAR' },
      ],
      bufferViews: [
        { buffer: 0, byteOffset: 0, byteLength: points.byteLength },
        { buffer: 0, byteOffset: points.byteLength, byteLength: polys.byteLength },
      ],
      buffers: [{ byteLength: points.byteLength + polys.byteLength }],
    };

    // Заполняем min/max
    for (let i=0; i<points.length; i+=3) {
      if (points[i] < gltf.accessors[0].min[0]) gltf.accessors[0].min[0] = points[i];
      if (points[i+1] < gltf.accessors[0].min[1]) gltf.accessors[0].min[1] = points[i+1];
      if (points[i+2] < gltf.accessors[0].min[2]) gltf.accessors[0].min[2] = points[i+2];
      if (points[i] > gltf.accessors[0].max[0]) gltf.accessors[0].max[0] = points[i];
      if (points[i+1] > gltf.accessors[0].max[1]) gltf.accessors[0].max[1] = points[i+1];
      if (points[i+2] > gltf.accessors[0].max[2]) gltf.accessors[0].max[2] = points[i+2];
    }

    if (normals?.length > 0) {
      gltf.meshes[0].primitives[0].attributes.NORMAL = 2;
      gltf.accessors.push({ bufferView: 2, componentType: 5126, count: normals.length / 3, type: 'VEC3' });
      gltf.bufferViews.push({ buffer: 0, byteOffset: points.byteLength + polys.byteLength, byteLength: normals.byteLength });
      gltf.buffers[0].byteLength += normals.byteLength;
    }

    const jsonStr = JSON.stringify(gltf);
    let blob;
    if (binary) {
      const jsonBuf = new TextEncoder().encode(jsonStr);
      const pad = jsonBuf.length % 4 ? 4 - jsonBuf.length % 4 : 0;
      const headerLen = 12 + 8 + jsonBuf.length + pad;
      const binLen = points.byteLength + polys.byteLength + (normals?.byteLength || 0);
      const totalLen = headerLen + 8 + binLen;
      const buf = new ArrayBuffer(totalLen);
      const dv = new DataView(buf);
      dv.setUint32(0, 0x46546C67, true); dv.setUint32(4, 2, true); dv.setUint32(8, totalLen, true);
      dv.setUint32(12, jsonBuf.length + pad, true); dv.setUint32(16, 0x4E4F534A, true);
      new Uint8Array(buf, 20).set(jsonBuf);
      dv.setUint32(headerLen, binLen, true); dv.setUint32(headerLen + 4, 0x004E4942, true);
      new Uint8Array(buf, headerLen + 8).set(new Uint8Array(points.buffer, points.byteOffset, points.byteLength));
      new Uint8Array(buf, headerLen + 8 + points.byteLength).set(new Uint8Array(polys.buffer, polys.byteOffset, polys.byteLength));
      if (normals) new Uint8Array(buf, headerLen + 8 + points.byteLength + polys.byteLength).set(new Uint8Array(normals.buffer, normals.byteOffset, normals.byteLength));
      blob = new Blob([buf], { type:'model/gltf-binary' });
    } else {
      const bin = new Uint8Array(points.byteLength + polys.byteLength + (normals?.byteLength||0));
      bin.set(new Uint8Array(points.buffer, points.byteOffset, points.byteLength));
      bin.set(new Uint8Array(polys.buffer, polys.byteOffset, polys.byteLength), points.byteLength);
      if (normals) bin.set(new Uint8Array(normals.buffer, normals.byteOffset, normals.byteLength), points.byteLength + polys.byteLength);
      const binStr = btoa(String.fromCharCode(...bin));
      gltf.buffers[0].uri = `data:application/octet-stream;base64,${binStr}`;
      blob = new Blob([JSON.stringify(gltf)], { type:'model/gltf+json' });
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=binary?filename.replace(/\.gltf$/,'.glb'):filename; a.click(); URL.revokeObjectURL(url);
    return { success:true, sizeKb:Math.round(blob.size/1024), format: binary?'glb':'gltf' };
  } catch(e) { log.warn('[vtkModule] exportGLTF:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// 2c. VRML EXPORT
// ══════════════════════════════════════════════════════════════

export function exportVRML(meshResult, filename = 'model.wrl') {
  const { points, polys } = meshResult;
  if (!points?.length) throw new Error('No mesh data');
  const lines = ['#VRML V2.0 utf8', 'Shape { geometry IndexedFaceSet { coord Coordinate { point ['];

  for (let i=0; i<points.length; i+=3) lines.push(`${points[i].toFixed(4)} ${points[i+1].toFixed(4)} ${points[i+2].toFixed(4)},`);
  lines.push('] } coordIndex [');

  if (polys) {
    let j=0;
    while (j<polys.length) { const n=polys[j]; j++; if (n>=3) { lines.push(polys.slice(j, j+n).join(', ')+', -1,'); } j+=n; }
  }

  lines.push('] } }');
  const text = lines.join('\n');
  const blob = new Blob([text], { type:'model/vrml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
  return { success:true, sizeKb:Math.round(text.length/1024) };
}

// ══════════════════════════════════════════════════════════════
// 3. VESSEL CENTERLINE
// ══════════════════════════════════════════════════════════════

export function extractCenterline(pixels, dims, spacing, startPoint, endPoint, options = {}) {
  const { huMin=150, huMax=600 } = options;
  const [w,h,d] = dims; const [dx,dy,dz] = spacing;
  const strideY=w, strideZ=w*h, totalVoxels=strideZ*d;
  const inBounds=(x,y,z)=>x>=0&&x<w&&y>=0&&y<h&&z>=0&&z<d;
  const cost=(x,y,z)=>{if(!inBounds(x,y,z))return Infinity;const hu=pixels[z*strideZ+y*strideY+x];if(hu<huMin||hu>huMax)return 1000;return Math.max(1,400-hu);};
  const [sx,sy,sz]=startPoint,[ex,ey,ez]=endPoint;
  const startIdx=sz*strideZ+sy*strideY+sx, endIdx=ez*strideZ+ey*strideY+ex;
  const heuristic=(x,y,z)=>{const ddx=(x-ex)*dx,ddy=(y-ey)*dy,ddz=(z-ez)*dz;return Math.sqrt(ddx*ddx+ddy*ddy+ddz*ddz);};

  const MAX_FLOAT=3.4028235e38;
  const gCost=new Float32Array(totalVoxels).fill(MAX_FLOAT), prevArr=new Int32Array(totalVoxels).fill(-1);
  gCost[startIdx]=0;
  const pq=new MinHeap(Math.min(totalVoxels,1000000));
  pq.push(heuristic(sx,sy,sz),0,startIdx);
  const neighbors6=[[1,0,0,dx],[-1,0,0,dx],[0,1,0,dy],[0,-1,0,dy],[0,0,1,dz],[0,0,-1,dz]];
  let iterations=0; const maxIterations=500000;

  while(pq.length>0&&iterations<maxIterations){iterations++;const entry=pq.pop();if(!entry)break;
    const currG=entry.g,currIdx=entry.val;if(currIdx===endIdx)break;if(currG>gCost[currIdx])continue;
    const cz=(currIdx/strideZ)|0,rem=currIdx-cz*strideZ,cy=(rem/strideY)|0,cx=rem-cy*strideY;
    for(let ni=0;ni<6;ni++){const[ndx,ndy,ndz,physDist]=neighbors6[ni];const nx=cx+ndx,ny=cy+ndy,nz=cz+ndz;if(!inBounds(nx,ny,nz))continue;
      const nIdx=nz*strideZ+ny*strideY+nx;const edgeCost=cost(nx,ny,nz)*physDist;if(edgeCost===Infinity)continue;
      const newG=currG+edgeCost;if(newG<gCost[nIdx]){gCost[nIdx]=newG;prevArr[nIdx]=currIdx;pq.push(newG+heuristic(nx,ny,nz),newG,nIdx);}}}

  if(gCost[endIdx]===MAX_FLOAT)return{success:false,path:[],error:'No path found'};
  const path=[];const idxПути=[];let currIdx=endIdx;
  while(currIdx!==-1&&currIdx!==startIdx){const cz=(currIdx/strideZ)|0,rem=currIdx-cz*strideZ,cy=(rem/strideY)|0,cx=rem-cy*strideY;path.push([cx*dx,cy*dy,cz*dz]);idxПути.push(currIdx);currIdx=prevArr[currIdx];}
  path.push([sx*dx,sy*dy,sz*dz]);idxПути.push(startIdx);path.reverse();idxПути.reverse();
  let lengthMm=0;for(let i=1;i<path.length;i++)lengthMm+=vec3.dist(path[i-1],path[i]);

  // [10.19.11] ЧЕСТНЫЙ ПРИЗНАК НАДЁЖНОСТИ. Стоимость выхода за окно яркости —
  // 1000, а не бесконечность: это сделано нарочно, чтобы линия могла
  // перескочить сужение или кальцинат. Побочное следствие было опасным: путь
  // находился ВСЕГДА, даже когда сосуда нет вовсе, и success:true ничего не
  // говорил о качестве. Тот же приём, что в rigidRegister: считаем долю точек,
  // попавших в сосуд, и говорим прямо — можно доверять или нет.
  let вСосуде=0;
  for(const i2 of idxПути){const hu=pixels[i2];if(hu>=huMin&&hu<=huMax)вСосуде++;}
  const доляВСосуде=idxПути.length?вСосуде/idxПути.length:0;
  const надёжно=доляВСосуде>=0.9;
  return{success:true,path,lengthMm:Math.round(lengthMm*10)/10,pointCount:path.length,iterations,
    доляВСосуде:Math.round(доляВСосуде*1000)/1000,надёжно,
    причина:надёжно?'линия идёт по сосуду':`только ${Math.round(доляВСосуде*100)}% точек попали в окно яркости — линии доверять нельзя`};
}

// ══════════════════════════════════════════════════════════════
// 3b. VESSEL BRANCH DETECTION
// ══════════════════════════════════════════════════════════════

export function detectVesselBranches(centerline, angleThresholdDeg = 30) {
  if (centerline.length < 3) return [];
  const branches = [];
  for (let i=1; i<centerline.length-1; i++) {
    const prev = vec3.sub(centerline[i], centerline[i-1]);
    const next = vec3.sub(centerline[i+1], centerline[i]);
    const angle = vec3.angle(prev, next);
    if (angle > angleThresholdDeg) {
      branches.push({ index: i, position: centerline[i], angleDeg: +angle.toFixed(1), direction: vec3.normalize(next) });
    }
  }
  return branches;
}

// ══════════════════════════════════════════════════════════════
// 3c. STENT PLANNING
// ══════════════════════════════════════════════════════════════

export function planStent(centerline, diametersAlongVessel) {
  if (centerline.length < 2 || !diametersAlongVessel?.length) return null;
  let totalLength = 0;
  for (let i=1; i<centerline.length; i++) totalLength += vec3.dist(centerline[i-1], centerline[i]);

  const avgDiameter = diametersAlongVessel.reduce((a,b)=>a+b,0)/diametersAlongVessel.length;
  const minDiameter = Math.min(...diametersAlongVessel);
  const maxDiameter = Math.max(...diametersAlongVessel);

  let stenosisStart = -1, stenosisEnd = -1, maxStenosis = 0;
  for (let i=0; i<diametersAlongVessel.length; i++) {
    const stenosis = (1 - diametersAlongVessel[i]/maxDiameter)*100;
    if (stenosis > 50 && stenosisStart < 0) stenosisStart = i;
    if (stenosis > 50) { stenosisEnd = i; maxStenosis = Math.max(maxStenosis, stenosis); }
  }

  let stenosisLengthMm = 0;
  if (stenosisStart >= 0 && stenosisEnd > stenosisStart) {
    const t0 = stenosisStart/(diametersAlongVessel.length-1);
    const t1 = stenosisEnd/(diametersAlongVessel.length-1);
    stenosisLengthMm = totalLength*(t1-t0);
  }

  return {
    vesselLengthMm: +totalLength.toFixed(1),
    avgDiameterMm: +avgDiameter.toFixed(1),
    minDiameterMm: +minDiameter.toFixed(1),
    maxDiameterMm: +maxDiameter.toFixed(1),
    stenosisPercent: +maxStenosis.toFixed(1),
    stenosisLengthMm: +stenosisLengthMm.toFixed(1),
    recommendedStentDiameterMm: Math.ceil(maxDiameter*1.1),
    recommendedStentLengthMm: Math.ceil(stenosisLengthMm + 10),
  };
}

// ══════════════════════════════════════════════════════════════
// 3d. ANEURYSM NECK MEASUREMENT
// ══════════════════════════════════════════════════════════════

export function measureAneurysmNeck(centerline, aneurysmStartIdx, aneurysmEndIdx) {
  if (!centerline || aneurysmStartIdx >= aneurysmEndIdx) return null;
  const neckStart = centerline[aneurysmStartIdx];
  const neckEnd = centerline[aneurysmEndIdx];
  const neckLength = vec3.dist(neckStart, neckEnd);
  const sacPoints = centerline.slice(aneurysmStartIdx, aneurysmEndIdx+1);
  let maxSacDiameter = 0, maxSacPoint = null;
  for (const pt of sacPoints) {
    const d = vec3.dist(pt, neckStart);
    if (d > maxSacDiameter) { maxSacDiameter = d; maxSacPoint = pt; }
  }
  return { neckLengthMm: +neckLength.toFixed(1), maxSacDiameterMm: +maxSacDiameter.toFixed(1), neckStart, neckEnd, maxSacPoint };
}

// ══════════════════════════════════════════════════════════════
// 4. CPR
// ══════════════════════════════════════════════════════════════

export function buildCPR(pixels, dims, spacing, centerline, halfWidth=20, sampleDist=1) {
  const [w,h,d]=dims; const [dx,dy,dz]=spacing;
  if(centerline.length<2)return{success:false,error:'Centerline too short'};
  const lengths=[0];for(let i=1;i<centerline.length;i++)lengths.push(lengths[i-1]+Math.max(vec3.dist(centerline[i-1],centerline[i]),0.001));
  const totalLength=lengths[lengths.length-1];if(totalLength<=0)return{success:false,error:'Zero-length centerline'};
  const resampledCL=[];for(let t=0;t<=totalLength;t+=sampleDist){let seg=0;for(let i=0;i<lengths.length-1;i++){if(lengths[i+1]>=t){seg=i;break;}}const segLen=lengths[seg+1]-lengths[seg],alpha=segLen>0?(t-lengths[seg])/segLen:0;const[x1,y1,z1]=centerline[seg],[x2,y2,z2]=centerline[Math.min(seg+1,centerline.length-1)];resampledCL.push([x1+alpha*(x2-x1),y1+alpha*(y2-y1),z1+alpha*(z2-z1)]);}
  const cprWidth=Math.round(halfWidth*2/sampleDist),cprHeight=resampledCL.length;if(cprHeight===0||cprWidth===0)return{success:false,error:'Empty CPR'};
  const cprPixels=new Int16Array(cprWidth*cprHeight).fill(-1024);
  const strideY=w,strideZ=w*h,invDx=1/dx,invDy=1/dy,invDz=1/dz;
  for(let si=0;si<resampledCL.length;si++){const[cx,cy,cz]=resampledCL[si];const next=resampledCL[Math.min(si+1,resampledCL.length-1)],prev=resampledCL[Math.max(si-1,0)];const dir=vec3.normalize([next[0]-prev[0],next[1]-prev[1],next[2]-prev[2]]);const up=Math.abs(dir[2])>0.9?[1,0,0]:[0,0,1];const pVec=vec3.normalize(vec3.cross(dir,up));const rowOff=si*cprWidth;
    for(let wi=0;wi<cprWidth;wi++){const off=(wi-cprWidth/2)*sampleDist;const vx=(cx+pVec[0]*off)*invDx,vy=(cy+pVec[1]*off)*invDy,vz=(cz+pVec[2]*off)*invDz;const x0=Math.floor(vx),y0=Math.floor(vy),z0=Math.floor(vz);if(x0<0||y0<0||z0<0||x0+1>=w||y0+1>=h||z0+1>=d)continue;const fx=vx-x0,fy=vy-y0,fz=vz-z0;const base=z0*strideZ+y0*strideY+x0;const b001=base+1,b010=base+strideY,b011=b010+1,b100=base+strideZ,b101=b100+1,b110=b100+strideY,b111=b110+1;const fx1=1-fx,fy1=1-fy,fz1=1-fz;const w00=fx1*fy1,w10=fx*fy1,w01=fx1*fy,w11=fx*fy;const val=pixels[base]*w00*fz1+pixels[b001]*w10*fz1+pixels[b010]*w01*fz1+pixels[b011]*w11*fz1+pixels[b100]*w00*fz+pixels[b101]*w10*fz+pixels[b110]*w01*fz+pixels[b111]*w11*fz;cprPixels[rowOff+wi]=(val+0.5)|0;}}
  return{success:true,pixels:cprPixels,width:cprWidth,height:cprHeight,physicalWidthMm:halfWidth*2,physicalHeightMm:totalLength,pixelSpacing:[sampleDist,sampleDist]};
}

// ══════════════════════════════════════════════════════════════
// 4b. MULTI-VESSEL CPR
// ══════════════════════════════════════════════════════════════

export function buildMultiVesselCPR(pixels, dims, spacing, centerlines, labels, halfWidth=10, sampleDist=1) {
  if (!centerlines?.length) return null;
  const results = [];
  for (let i=0; i<centerlines.length; i++) {
    const cpr = buildCPR(pixels, dims, spacing, centerlines[i], halfWidth, sampleDist);
    if (cpr.success) results.push({ ...cpr, label: labels?.[i] || `Vessel ${i+1}`, index: i });
  }
  return { success: true, vessels: results, count: results.length };
}

export function renderCPRToCanvas(cprResult, canvas, ww=1500, wc=300) {
  if(!cprResult.success)return;
  const{pixels,width,height}=cprResult;const ctx=canvas.getContext('2d');canvas.width=width;canvas.height=height;
  const imageData=ctx.createImageData(width,height);const lo=wc-ww/2,scale=255/(ww||1),len=pixels.length;const rgba=imageData.data;
  for(let i=0;i<len;i++){let v=((pixels[i]-lo)*scale+0.5)|0;if(v<0)v=0;else if(v>255)v=255;const off=i*4;rgba[off]=v;rgba[off+1]=v;rgba[off+2]=v;rgba[off+3]=255;}
  ctx.putImageData(imageData,0,0);
}

// ══════════════════════════════════════════════════════════════
// 5. АОРТА
// ══════════════════════════════════════════════════════════════

export function measureAortaDiameter(slicePixels, w, h, spacing, point, huThreshold=150) {
  const[px,py]=point;const[dx,dy]=spacing;const visited=new Uint8Array(w*h);const capacity=w*h;const queueX=new Uint16Array(capacity),queueY=new Uint16Array(capacity);let head=0,tail=0,count=0;queueX[tail]=px;queueY[tail]=py;tail=(tail+1)%capacity;count++;visited[py*w+px]=1;let minX=px,maxX=px,minY=py,maxY=py,area=0;const ndx=[1,-1,0,0],ndy=[0,0,1,-1];
  while(count>0){const cx=queueX[head],cy=queueY[head];head=(head+1)%capacity;count--;area++;if(cx<minX)minX=cx;if(cx>maxX)maxX=cx;if(cy<minY)minY=cy;if(cy>maxY)maxY=cy;for(let ni=0;ni<4;ni++){const nx=cx+ndx[ni],ny=cy+ndy[ni];if(nx<0||nx>=w||ny<0||ny>=h)continue;const nIdx=ny*w+nx;if(!visited[nIdx]&&slicePixels[nIdx]>=huThreshold){visited[nIdx]=1;queueX[tail]=nx;queueY[tail]=ny;tail=(tail+1)%capacity;count++;if(count>=capacity)break;}}if(count>=capacity)break;}
  const diamX=(maxX-minX)*dx,diamY=(maxY-minY)*dy,diamMean=(diamX+diamY)/2;const areaMm2=area*dx*dy,diamFromArea=2*Math.sqrt(Math.max(0,areaMm2)/Math.PI);
  return{diamX:Math.round(diamX*10)/10,diamY:Math.round(diamY*10)/10,diamMean:Math.round(diamMean*10)/10,diamFromArea:Math.round(diamFromArea*10)/10,areaMm2:Math.round(areaMm2*10)/10,area,bbox:[minX,minY,maxX,maxY],mask:visited};
}

// ══════════════════════════════════════════════════════════════
// 6. 3D MEASUREMENTS ON MESH
// ══════════════════════════════════════════════════════════════

export function measure3DOnMesh(meshResult, pointIndexA, pointIndexB) {
  const { points } = meshResult;
  if (!points || pointIndexA >= points.length/3 || pointIndexB >= points.length/3) return null;
  const a = [points[pointIndexA*3], points[pointIndexA*3+1], points[pointIndexA*3+2]];
  const b = [points[pointIndexB*3], points[pointIndexB*3+1], points[pointIndexB*3+2]];
  return { distanceMm: +vec3.dist(a,b).toFixed(2), pointA: a, pointB: b, indexA: pointIndexA, indexB: pointIndexB };
}

export function findClosestMeshPoint(meshResult, worldPos) {
  const { points } = meshResult;
  if (!points) return null;
  let bestIdx = -1, bestDist = Infinity;
  for (let i=0; i<points.length/3; i++) {
    const p = [points[i*3], points[i*3+1], points[i*3+2]];
    const d = vec3.dist(p, worldPos);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  return bestIdx >= 0 ? { index: bestIdx, point: [points[bestIdx*3], points[bestIdx*3+1], points[bestIdx*3+2]], distanceMm: +bestDist.toFixed(2) } : null;
}

// ══════════════════════════════════════════════════════════════
// 7. ENTERPRISE
// ══════════════════════════════════════════════════════════════

export async function runMultiThresholdMC(pixels, dims, spacing, isoValues=[150,300,700]) {
  try{const vtk=await getVtk();const{vtkImageData,dataArray}=await createVtkImageData(pixels,dims,spacing);const results={};for(const iso of isoValues){try{const mc=vtk.vtkImageMarchingCubes.newInstance({contourValue:iso,computeNormals:false,mergePoints:true});mc.setInputData(vtkImageData);mc.update();const pd=mc.getOutputData();results[iso]={pointCount:pd.getNumberOfPoints(),triangleCount:pd.getNumberOfCells(),points:Float32Array.from(pd.getPoints().getData()),polys:Uint32Array.from(pd.getPolys().getData())};mc.delete();pd.delete();}catch(e){results[iso]={error:e.message,pointCount:0};}}dataArray.delete();vtkImageData.delete();return results;}catch(e){log.warn('[vtkModule] runMultiThresholdMC:',e.message);return{};}
}

export function exportOBJ(meshResult, filename='model.obj') {
  const{points,polys,normals}=meshResult;if(!points?.length)throw new Error('No mesh data');const lines=['# Mikhmel Cornerstone4D DICOM Viewer — OBJ export',`# Vertices: ${points.length/3}`,''];for(let i=0;i<points.length;i+=3)lines.push(`v ${points[i].toFixed(4)} ${points[i+1].toFixed(4)} ${points[i+2].toFixed(4)}`);if(normals?.length>0)for(let i=0;i<normals.length;i+=3)lines.push(`vn ${normals[i].toFixed(4)} ${normals[i+1].toFixed(4)} ${normals[i+2].toFixed(4)}`);if(polys){let j=0;while(j<polys.length){const n=polys[j];j++;if(n<3){j+=n;continue;}const baseIdx=j;for(let t=1;t<n-1;t++){const a=polys[baseIdx]+1,b=polys[baseIdx+t]+1,c=polys[baseIdx+t+1]+1;lines.push(normals?.length>0?`f ${a}//${a} ${b}//${b} ${c}//${c}`:`f ${a} ${b} ${c}`);}j+=n;}}const text=lines.join('\n');const blob=new Blob([text],{type:'text/plain'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=filename;a.click();URL.revokeObjectURL(url);return{success:true,sizeKb:Math.round(text.length/1024),vertexCount:points.length/3};
}

export function computeVolumeByThreshold(pixels, dims, spacing, lower, upper) {
  const[w,h,d]=dims;const voxelVolume=spacing[0]*spacing[1]*spacing[2];let count=0;const total=w*h*d;for(let i=0;i<total;i++){if(pixels[i]>=lower&&pixels[i]<=upper)count++;}return{voxelCount:count,volumeMm3:+(count*voxelVolume).toFixed(1),volumeCc:+((count*voxelVolume)/1000).toFixed(2),percentage:+((count/total)*100).toFixed(1),thresholdRange:[lower,upper]};
}

export function extractMultipleCenterlines(pixels, dims, spacing, startPoint, endPoints, options={}) {
  const results=[];for(const ep of endPoints)results.push(extractCenterline(pixels,dims,spacing,startPoint,ep,options));const successful=results.filter(r=>r.success);return{total:endPoints.length,successful:successful.length,totalLengthMm:+successful.reduce((s,r)=>s+r.lengthMm,0).toFixed(1),paths:results};
}

export async function isVtkAvailable(){try{await getVtk();return true;}catch{return false;}}

export function getVtkModuleState(){return{vtkLoaded:vtklib!==null,features:['marchingCubes','decimateMesh','smoothMesh','stlExport','gltfExport','vrmlExport','objExport','centerline','branchDetection','stentPlanning','aneurysmNeck','cpr','multiVesselCPR','aortaDiameter','mesh3DMeasurements','multiThresholdMC','volumeByThreshold','batchCenterline'],featureCount:18};}